import {
  Avatar,
  DataTable,
  EmptyState,
  MasterDetail,
  PageHeader,
  Pagination,
  SearchField,
  StatusBadge,
  UnderlineTabs,
} from '@hemmafixa/ui';
import { ClipboardCheck, MousePointerClick } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { formatPostalCode, useCategoryLabel } from '../shared/format';
import { DetailPanel, FullPageLink, SelectableCard } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useSelectedParam, useShownSelection } from '../shared/use-selected-param';
import { useReviewQueue } from './api';
import { ReviewJobDetail } from './review-job-page';

type Filter = 'all' | 'in_review' | 'needs_info';
const PAGE_SIZE = 20;

/** Jobs waiting for review, oldest first (P3-10), with the selected job beside the list (UI-07). */
export function ReviewQueuePage() {
  const { t } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const [picked, setSelected] = useSelectedParam();
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [searchQuery, setSearchQuery] = useState(search);
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const r = t.admin.review;

  // Ask the API once typing pauses, not on every key
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useReviewQueue({
    status: filter === 'all' ? undefined : filter,
    q: searchQuery || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const selected = useShownSelection(
    picked,
    query.data?.items.map((item) => item.id),
  );

  return (
    <div className="grid gap-4">
      <PageHeader
        title={r.title}
        description={r.description}
        actions={
          <SearchField
            className="w-full sm:w-96 xl:w-[28rem]"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={r.searchPlaceholder}
          />
        }
      />
      <div className="flex items-center justify-between gap-3">
        <UnderlineTabs
          label={r.columns.status}
          value={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
          options={(['all', 'in_review', 'needs_info'] as const).map((f) => ({
            value: f,
            label: r.filters[f],
            count: f === filter ? query.data?.total : undefined,
          }))}
        />
        {/* Beside the tabs, so the details start level with the list */}
        {selected && (
          <FullPageLink to={`/admin/review/${selected}`} className="hidden shrink-0 lg:inline-flex" />
        )}
      </div>
      <MasterDetail
        divided
        open={selected !== null}
        onClose={() => setSelected(null)}
        labels={{ details: r.details }}
        placeholder={<EmptyState icon={MousePointerClick} title={t.common.pickItem} />}
        detail={
          selected ? (
            <DetailPanel fullPage={`/admin/review/${selected}`} linkBesideTabs>
              <ReviewJobDetail id={selected} />
            </DetailPanel>
          ) : null
        }
        list={
          <>
            <QueryView query={query}>
              {(result) => (
                <>
                  <DataTable
                    layout="cards"
                    columns={[]}
                    data={result.items}
                    getRowId={(job) => job.id}
                    renderCard={(job) => (
                      <SelectableCard
                        to={`/admin/review/${job.id}`}
                        selected={job.id === selected}
                        onSelect={() => setSelected(job.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar seed={job.id} name={job.title} kind="company" size="md" />
                          <div className="grid min-w-0 flex-1 gap-0.5">
                            <p className="truncate font-semibold text-secondary">{job.title}</p>
                            <p className="truncate text-sm text-muted-foreground">
                              {[categoryLabel(job.categorySlug), formatPostalCode(job.postalCode)]
                                .filter(Boolean)
                                .join(' • ')}
                            </p>
                          </div>
                          <StatusBadge status={job.status} statuses={statuses.job} />
                        </div>
                      </SelectableCard>
                    )}
                    empty={
                      search !== '' || filter !== 'all' ? (
                        <EmptyState
                          icon={ClipboardCheck}
                          title={t.common.noMatchesTitle}
                          description={t.common.noMatchesText}
                        />
                      ) : (
                        <EmptyState icon={ClipboardCheck} title={r.empty} description={r.emptyText} />
                      )
                    }
                  />
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              )}
            </QueryView>
          </>
        }
      />
    </div>
  );
}
